import React, { useContext, useEffect, useState } from 'react';
import api from '../utils/api';
import Modal from './Modal';
import IconButton from './IconButton';
import AuthContext from '../contexts/AuthContext';
import { useToast } from './Toast';
import { relativeTime } from '../utils/relativeTime';

const LABEL = 'block text-[10px] font-bold uppercase tracking-[0.05em] text-surface-muted mb-1';
const INPUT = 'w-full px-[10px] py-2 bg-surface-base text-surface-text border border-surface-border text-xs focus:outline-none focus:ring-2 focus:ring-accent rounded-[2px]';

/**
 * NotesModal — append-only note history for one account.
 * Shows newest note first. Viewers are read-only; User+ can add notes; a note
 * can be deleted by its author or any manager.
 */
export default function NotesModal({ account, onClose, onMutated }) {
  const { role, userId } = useContext(AuthContext);
  const { push: toast } = useToast();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/api/accounts/${account._id}/notes`);
      setNotes(resp.data);
      setError('');
    } catch {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, [account._id]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await api.post(`/api/accounts/${account._id}/notes`, { text: trimmed });
      setText('');
      toast({ kind: 'success', message: 'Note added.' });
      await fetchNotes();
      if (onMutated) onMutated();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to add note.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await api.delete(`/api/accounts/${account._id}/notes/${noteId}`);
      toast({ kind: 'success', message: 'Note deleted.' });
      await fetchNotes();
      if (onMutated) onMutated();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to delete note.' });
    }
  };

  return (
    <Modal title={`Notes · ${account.displayName || account.steamId64}`} onClose={onClose}>
      {role !== 'viewer' && (
        <form onSubmit={handleAdd} className="mb-4">
          <label className={LABEL}>Add a note</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="What was this account used for?"
            rows={3}
            maxLength={2000}
            className={`${INPUT} resize-y`}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] text-surface-muted">{text.length} / 2000</div>
            <button
              type="submit"
              disabled={submitting || !text.trim()}
              className="px-[14px] py-2 bg-accent text-surface-base border-0 text-[11px] font-bold uppercase tracking-wider rounded-[2px] disabled:opacity-40 disabled:cursor-not-allowed"
            >{submitting ? 'Saving…' : 'Add note'}</button>
          </div>
        </form>
      )}

      <div className="border-t border-surface-border pt-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-surface-muted mb-2">History</div>
        {loading && <div className="text-xs text-surface-muted py-4">Loading…</div>}
        {error && <div className="text-xs text-steam-danger py-2">{error}</div>}
        {!loading && notes.length === 0 && !error && (
          <div className="text-xs text-surface-muted py-4">No notes yet.</div>
        )}
        <div className="space-y-2 max-h-[360px] overflow-y-auto">
          {notes.map((n) => {
            const when = relativeTime(n.createdAt);
            return (
              <div key={n._id} className="bg-surface-base border border-surface-border p-3 rounded-[2px]">
                <div className="text-xs text-surface-text whitespace-pre-wrap break-words">{n.text}</div>
                <div className="flex items-center justify-between mt-2 text-[10px] text-surface-muted">
                  <div>
                    <span className="text-accent">{n.createdBy?.username || 'unknown'}</span>
                    <span> · </span>
                    <span title={when.absolute}>{when.relative}</span>
                  </div>
                  {(role === 'manager' ||
                    (userId != null && n.createdBy?._id != null &&
                     String(n.createdBy._id) === String(userId))) && (
                    <IconButton icon="✕" color="danger" onClick={() => handleDelete(n._id)} title="Delete note" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
